import { Copy, Download, Link2, QrCode } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { SurveyDetail } from '../../api/types';
import { useStoredState } from '../../hooks/useStoredState';
import { useI18n } from '../../i18n';
import { download, parseLogo, qrPng, qrSvg, svgDataUrl, type QrLogo } from '../../qr';
import { Field, Switch, useToast } from '../ui';

const SLUG_MIN = 3;
const SLUG_MAX = 40;
const SLUG_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** What the server stores: lower case, no surrounding space. */
export const normalizeSlug = (s: string): string => s.trim().toLowerCase();

/** True when `slug` (already normalized) may be sent; the empty slug removes the link. */
export function slugIsValid(slug: string): boolean {
  return slug === '' || (slug.length >= SLUG_MIN && slug.length <= SLUG_MAX && SLUG_SHAPE.test(slug));
}

let logoPromise: Promise<QrLogo | null> | null = null;
/** The brand mark for the QR code, fetched once. */
function loadLogo(): Promise<QrLogo | null> {
  logoPromise ??= fetch('/brand/logo-mark.svg').then((r) => (r.ok ? r.text() : '')).then((svg) => parseLogo(svg), () => null);
  return logoPromise;
}

/**
 * The questionnaire's public link: a custom name after /s/ next to the random
 * code (both keep working), and a QR code for whichever link is shown.
 */
export function ShareLinkCard({ survey, canEdit, onSurveyUpdate }: { survey: SurveyDetail; canEdit: boolean; onSurveyUpdate: (s: SurveyDetail) => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const origin = window.location.origin;
  const [draft, setDraft] = useState(survey.slug ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [withLogo, setWithLogo] = useStoredState('survey.share.qr_logo', false);
  const [logo, setLogo] = useState<QrLogo | null>(null);
  useEffect(() => setDraft(survey.slug ?? ''), [survey.slug]);
  useEffect(() => {
    if (withLogo) void loadLogo().then(setLogo);
  }, [withLogo]);

  const slug = normalizeSlug(draft);
  const valid = slugIsValid(slug);
  const changed = slug !== (survey.slug ?? '');
  const link = `${origin}/s/${survey.slug || survey.code}`;
  const options = useMemo(() => ({ logo: withLogo && logo ? logo : undefined }), [withLogo, logo]);
  const svg = useMemo(() => qrSvg(link, options), [link, options]);
  const fileName = `${survey.slug || survey.code}-qr`;

  async function save(next: string) {
    setSaving(true);
    setError('');
    try {
      const res = await api.setSlug(survey.id, next);
      onSurveyUpdate({ ...survey, slug: res.slug || undefined });
      toast(t(res.slug ? 'share.slug_saved' : 'share.slug_removed'));
    } catch (err) {
      if (err instanceof ApiError && err.fields.slug) setError(err.fields.slug);
      else toast(err instanceof ApiError ? err.message : t('common.action_failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  const copy = () =>
    void navigator.clipboard?.writeText(link).then(
      () => toast(t('share.copied')),
      () => toast(t('share.copy_failed'), 'error'),
    );

  return (
    <div className="card">
      <div className="card-head">
        <div className="tile">
          <Link2 />
        </div>
        <div className="grow">
          <h2>{t('share.link_title')}</h2>
          <div className="sub">{t('share.link_sub')}</div>
        </div>
      </div>
      <div className="card-pad stack gap-16" style={{ paddingTop: 0 }}>
        <form
          className="stack gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            void save(slug);
          }}
        >
          <Field
            label={t('share.custom_link')}
            htmlFor="custom-slug"
            error={error || (!valid ? t('share.slug_invalid', { min: SLUG_MIN, max: SLUG_MAX }) : undefined)}
            hint={t('share.slug_hint')}
          >
            <div className="slug-input">
              <span className="prefix">{origin.replace(/^https?:\/\//, '')}/s/</span>
              <input
                id="custom-slug"
                className="input"
                value={draft}
                maxLength={SLUG_MAX + 10}
                disabled={!canEdit}
                placeholder={t('share.slug_placeholder')}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setError('');
                }}
              />
            </div>
          </Field>
          {canEdit && (
            <div className="row gap-8">
              <button className="btn primary sm" disabled={!changed || !valid || saving || slug === ''}>
                {t('common.save')}
              </button>
              {survey.slug && (
                <button type="button" className="btn sm" disabled={saving} onClick={() => void save('')}>
                  {t('share.slug_remove')}
                </button>
              )}
            </div>
          )}
          <p className="hint">{t('share.code_still_works', { link: `${origin}/s/${survey.code}` })}</p>
        </form>

        <div className="divider" style={{ margin: 0 }} />

        <div className="qr-block">
          <img className="qr-img" src={svgDataUrl(svg)} alt={t('share.qr_alt', { link })} width={176} height={176} />
          <div className="stack gap-8 grow">
            <div className="row gap-8">
              <QrCode />
              <strong>{t('share.qr_title')}</strong>
            </div>
            <p className="hint">{t('share.qr_hint')}</p>
            <Switch checked={withLogo} onChange={setWithLogo} label={t('share.qr_logo')} />
            <div className="btn-row">
              <button type="button" className="btn sm" onClick={() => void qrPng(link, 1024, options).then((b) => download(b, `${fileName}.png`), () => toast(t('common.action_failed'), 'error'))}>
                <Download /> PNG
              </button>
              <button type="button" className="btn sm" onClick={() => download(new Blob([qrSvg(link, { ...options, size: 1024 })], { type: 'image/svg+xml' }), `${fileName}.svg`)}>
                <Download /> SVG
              </button>
              <button type="button" className="btn sm" onClick={copy}>
                <Copy /> {t('share.copy_link')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
